import { getQuestionValue, isDontKnowValue, isPlainObjectAnswer, type ChartDataPoint } from '@/lib/utils/aggregation'
import { isInContinuousBin } from '@digital-twins/deck-ui/utils/continuousBinning'
import type { QualitativeQuote, QualitativeThemePackage } from '@digital-twins/shared-types'

/**
 * Per-theme result of {@link segmentThemeShares}: how many of the segment's
 * verbatims were coded to this theme, the segment's total verbatim count
 * (repeated on every entry, since it is one shared denominator), and the
 * resulting percent.
 */
export interface SegmentThemeShare {
  themeId: string
  /** Verbatims coded to this theme that fall in the segment. */
  frequency: number
  /** Total verbatims in the segment, across every theme (and uncoded ones). */
  base: number
  /** `frequency / base * 100`, or `0` when `base` is `0`. */
  percent: number
  /** In-segment verbatims coded to this theme, in `quoteCandidates` pool order. */
  quotes: Array<QualitativeQuote>
}

/**
 * Same precedence `QuestionCard.tsx`'s own `getResponseId` uses to name a
 * response row, duplicated here so this file stays a standalone pure module
 * (no import back onto `QuestionCard.tsx`).
 */
function resolveRespondentKey(response: any, index: number): string {
  return String(
    response?.respondent_id ?? response?.respondentId ?? response?.id ?? `response_${index + 1}`,
  )
}

type SegmentValue = {
  rawValue?: unknown
  numericBin?: ChartDataPoint['numericBin']
}

function matchesSegmentValue(rawValue: unknown, target: SegmentValue, segmentQuestion: any): boolean {
  if (rawValue === undefined || rawValue === null) return false
  if (isPlainObjectAnswer(rawValue)) return false
  if (Array.isArray(rawValue)) {
    return rawValue.some((entry) => matchesSegmentValue(entry, target, segmentQuestion))
  }
  if (target.numericBin) {
    // The question travels so its declared options win.
    if (isDontKnowValue(rawValue, segmentQuestion) || typeof rawValue === 'boolean') return false
    const value = Number(rawValue)
    return Number.isFinite(value) && isInContinuousBin(value, target.numericBin, target.numericBin.inclusiveHigh)
  }
  return String(rawValue) === String(target.rawValue)
}

/**
 * Segment-filtered theme shares for a themed open-end question.
 *
 * Join chain: theme -> `quoteIds` -> `quoteCandidates[].respondentId` -> response row (matched by
 * respondent id) -> `getQuestionValue(row, segmentQuestion)`.
 *
 * The base unit is VERBATIMS in the segment, not respondents: deliberate, because it is the same
 * unit the overall (unsegmented) theme `percent` divides by (`responseCount`), so the two numbers
 * reconcile. One monadic respondent can own several verbatims (e.g. one per asset lane on a
 * `per_asset` question); each of their in-segment verbatims counts toward the base separately. A
 * two-asset package pools naturally: `quoteCandidates` already holds every lane's verbatims, and
 * this function does not filter by `assetId`.
 *
 * Returns `null` when ANY theme on the package lacks `quoteIds` (packages stored before that
 * field existed) — the caller falls back to the unsegmented view rather than show a partial or
 * zeroed result. A verbatim with `respondentIdentityVerified === false` never counts toward the
 * segment base, in any theme, and a verbatim whose id cannot be resolved against
 * `quoteCandidates`, or whose `respondentId` cannot be joined to a response row, is excluded the
 * same way (unresolvable, not a segment member).
 *
 * Pure: reads its inputs, mutates nothing, does no I/O.
 */
export function segmentThemeShares(
  themePackage: QualitativeThemePackage | null | undefined,
  responses: Array<any>,
  segmentQuestion: any,
  segmentValue: SegmentValue | null | undefined,
): Array<SegmentThemeShare> | null {
  if (!themePackage?.themes?.length) return null
  if (themePackage.themes.some((theme) => theme.quoteIds == null)) return null
  if (!segmentQuestion || segmentValue?.rawValue == null) return null
  const target = segmentValue

  const candidatesById = new Map<string, QualitativeQuote>()
  for (const candidate of themePackage.quoteCandidates || []) {
    if (candidate?.id) candidatesById.set(candidate.id, candidate)
  }

  const responseByRespondentKey = new Map<string, any>()
  ;(responses || []).forEach((response, index) => {
    responseByRespondentKey.set(resolveRespondentKey(response, index), response)
  })

  function isInSegment(candidate: QualitativeQuote | undefined): boolean {
    if (!candidate) return false
    if (candidate.respondentIdentityVerified === false) return false
    if (!candidate.respondentId) return false
    const row = responseByRespondentKey.get(String(candidate.respondentId))
    if (!row) return false
    return matchesSegmentValue(getQuestionValue(row, segmentQuestion), target, segmentQuestion)
  }

  // The segment base pools every quote candidate the segment reaches, coded to a
  // theme or not, in quoteCandidates order — the same universe `responseCount`
  // draws from, just filtered to the segment.
  const eligibleIds = new Set<string>()
  for (const candidate of themePackage.quoteCandidates || []) {
    if (candidate?.id && isInSegment(candidate)) {
      eligibleIds.add(candidate.id)
    }
  }
  const base = eligibleIds.size

  return themePackage.themes.map((theme) => {
    const inSegmentIds = (theme.quoteIds || []).filter((id) => eligibleIds.has(id))
    const quotes = inSegmentIds
      .map((id) => candidatesById.get(id))
      .filter((quote): quote is QualitativeQuote => Boolean(quote))

    return {
      themeId: theme.id,
      frequency: inSegmentIds.length,
      base,
      percent: base > 0 ? (inSegmentIds.length / base) * 100 : 0,
      quotes,
    }
  })
}
